import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { conversationRoleSchema, conversationStatusSchema } from './schema';
import { clinicianSectionKeySchema } from './summary-artifacts';

// Existing voice-history wire contract. Source identity/status come from the
// dedicated source row, createdAt from the provider-neutral conversation, and
// duration from ordered messages. The API names remain stable while storage is
// normalized.

// The summary fields sourced directly from `conversations` columns — the subset
// tied to the table by the drift-guard test (response.drift.test.ts).
export const CONVERSATION_SUMMARY_SOURCE_COLUMN_FIELDS = [
  'externalConversationId',
  'externalAgentId',
  'status',
] as const;
export const CONVERSATION_SUMMARY_CORE_COLUMN_FIELDS = ['createdAt'] as const;

export const conversationSummarySchema = z.object({
  conversationId: z.string(),
  agentId: z.string().nullable(),
  agentType: z.string().nullable(),
  agentName: z.string().nullable(),
  status: conversationStatusSchema,
  createdAt: z.string(),
  durationSec: z.number().int().nonnegative(),
  // Whether this conversation raised a safety escalation. Server-derived by
  // joining the escalations table, so it belongs to neither drift-guarded
  // column set: not CONVERSATION_SUMMARY_SOURCE_COLUMN_FIELDS (columns of the
  // source row) nor CONVERSATION_SUMMARY_CORE_COLUMN_FIELDS (columns of
  // `conversations` itself). It is derived rather than stored, as `durationSec`
  // is — adding it to either set would make the drift guard demand a column
  // that does not and should not exist.
  escalationRaised: z.boolean(),
  // The stored clinician artifact's `highLevel` — the third-person overview
  // written for a care person. Null when no usable summary exists or the
  // clinician display gate is closed. Server-derived for the same reason as
  // `escalationRaised` above, so it is likewise in neither column set. The
  // member-facing `brief` / `memberDetail` artifacts are second-person copy
  // addressed to the member and deliberately never reach this staff surface.
  summary: z.string().nullable(),
});
export type ConversationSummary = z.infer<typeof conversationSummarySchema>;

// Paginated list body (keyset): `nextCursor` is null on the last page.
export const conversationListDataSchema = z.object({
  items: z.array(conversationSummarySchema),
  nextCursor: z.string().nullable(),
});
export type ConversationListData = z.infer<typeof conversationListDataSchema>;
export const conversationListResponseSchema = apiEnvelope(conversationListDataSchema);
export type ConversationListResponse = z.infer<typeof conversationListResponseSchema>;

// A single transcript turn — the simplified wire shape (flattened text), not the
// stored `EnrichedTurn` (which carries `ContentBlock[]`). `timestamp` is the
// turn's absolute Unix time.
export const transcriptTurnSchema = z.object({
  role: conversationRoleSchema,
  text: z.string(),
  timestamp: z.number().int().nonnegative(),
  // Position in the stored, source-exact, visibility-bounded message array —
  // assigned BEFORE empty-text (tool-only) turns are dropped, so these values
  // are non-contiguous whenever the conversation held tool turns. It lets a
  // citation name its turn by projection coordinate rather than by position in
  // this dense array. Not a durable identity for the underlying message: a
  // citation is only trustworthy alongside the read-time verification below.
  turnIndex: z.number().int().nonnegative(),
  // The turn's stored `messages.position` — the conversation's own ordering
  // key, unaffected by the visibility window `turnIndex` is counted within, so
  // a pointer stored elsewhere (an escalation's signal turn) can name this turn
  // durably. Optional because a client that ships ahead of the API reads turns
  // without it, and must then match nothing rather than fail the parse.
  position: z.number().int().nonnegative().optional(),
});
export type TranscriptTurn = z.infer<typeof transcriptTurnSchema>;

/**
 * One clinician claim as served. `turnIndex` is the stored citation's index —
 * both its provenance and its link target — and `evidenceLinkable` is the
 * server's read-time verdict on whether that index still verifies: the turn is
 * present in this response AND still contains the quote AND satisfies the
 * section's role rule.
 *
 * Deliberately one index and one boolean rather than a second "resolved"
 * index: two index fields could disagree, and a contradiction on a clinical
 * evidence affordance is worse than a less expressive payload.
 */
export const clinicianSummaryCitationSchema = z
  .object({
    text: z.string().min(1),
    quote: z.string().min(1),
    turnIndex: z.number().int().nonnegative(),
    evidenceLinkable: z.boolean(),
  })
  .strict();
export type ClinicianSummaryCitation = z.infer<typeof clinicianSummaryCitationSchema>;

export const clinicianSummarySectionSchema = z
  .object({
    sectionKey: clinicianSectionKeySchema,
    items: z.array(clinicianSummaryCitationSchema).min(1),
  })
  .strict();
export type ClinicianSummarySection = z.infer<typeof clinicianSummarySectionSchema>;

/**
 * Two independent facts about a summary's evidence, deliberately not merged.
 *
 * `claimsTotal` / `claimsDropped` are the GENERATION-time grounding audit and
 * are null when the stored `grounding` column is absent or unparseable —
 * never defaulted to zero, because zero asserts that nothing was dropped and
 * a missing audit does not support that claim. `evidenceAudited` says which
 * case the reader is in.
 *
 * `claimsUnlinkable` is computed by the READ path on every request and is
 * therefore always present. A clinician must be able to tell "the model's
 * claim was unverifiable when this was written" from "the evidence is not in
 * the transcript you are looking at".
 */
export const clinicianSummaryEvidenceSchema = z
  .object({
    claimsTotal: z.number().int().nonnegative().nullable(),
    claimsDropped: z.number().int().nonnegative().nullable(),
    claimsUnlinkable: z.number().int().nonnegative(),
    evidenceAudited: z.boolean(),
  })
  .strict();
export type ClinicianSummaryEvidence = z.infer<typeof clinicianSummaryEvidenceSchema>;

export const conversationClinicianSummarySchema = z
  .object({
    highLevel: z.string().min(1),
    sections: z.array(clinicianSummarySectionSchema),
    evidence: clinicianSummaryEvidenceSchema,
  })
  .strict();
export type ConversationClinicianSummary = z.infer<typeof conversationClinicianSummarySchema>;

export const conversationTranscriptDataSchema = z.object({
  conversationId: z.string(),
  agentId: z.string().nullable(),
  agentType: z.string().nullable(),
  agentName: z.string().nullable(),
  createdAt: z.string(),
  durationSec: z.number().int().nonnegative(),
  turns: z.array(transcriptTurnSchema),
  clinicianSummary: conversationClinicianSummarySchema.nullable(),
});
export type ConversationTranscriptData = z.infer<typeof conversationTranscriptDataSchema>;
export const conversationTranscriptResponseSchema = apiEnvelope(conversationTranscriptDataSchema);
export type ConversationTranscriptResponse = z.infer<typeof conversationTranscriptResponseSchema>;

/**
 * Why a conversation has no playable recording. `typed` means no audio object
 * exists and every member turn was typed; `not_captured` covers every other
 * absence, including a conversation with no linked voice session.
 */
export const conversationRecordingUnavailableReasonSchema = z.enum(['typed', 'not_captured']);
export type ConversationRecordingUnavailableReason = z.infer<
  typeof conversationRecordingUnavailableReasonSchema
>;

/**
 * Why a playback link is being asked for: the reader pressed play for the
 * first time, or a link already in use expired and is being replaced. Recorded
 * on the access audit so a year of records separates listens from refreshes.
 */
export const conversationRecordingIntentSchema = z.enum(['play', 'refresh']);
export type ConversationRecordingIntent = z.infer<typeof conversationRecordingIntentSchema>;

/**
 * The recording read's answer. Deliberately non-strict at every level: Atlas
 * parses it, and a field added later must not break an already-loaded bundle.
 * `ready` says a recording exists without handing out a link — the answer to a
 * request that named no intent, which is neither a listen nor audited.
 */
export const conversationRecordingDataSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('ready') }),
  z.object({
    status: z.literal('available'),
    url: z.string().url(),
    /** When the link stops working: an offset-bearing ISO-8601 instant the client compares against. */
    expiresAt: z.string().datetime({ offset: true }),
    contentType: z.literal('audio/mpeg'),
  }),
  z.object({ status: z.literal('pending') }),
  z.object({
    status: z.literal('unavailable'),
    reason: conversationRecordingUnavailableReasonSchema,
  }),
]);
export type ConversationRecordingData = z.infer<typeof conversationRecordingDataSchema>;
export const conversationRecordingResponseSchema = apiEnvelope(conversationRecordingDataSchema);
export type ConversationRecordingResponse = z.infer<typeof conversationRecordingResponseSchema>;
